import { addTranslations } from './../../i18n';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Answer from '../models/Answer';
import { FinishSurveyStatus } from '../models/FinishSurveyStatus';
import Question from '../models/Question';
import Survey from '../models/Survey';
import SurveyListResponse, { DemoGraphicSurveyQuestion } from '../models/SurveyListResponse';
import Language from '@/store/models/Language';

export const setup = () => {
    const store = useStore();
    const router = useRouter();
    const finishingSurvey = ref(false);

    const finishSurvey = async (surveyId: number): Promise<string> => {
        if (finishingSurvey.value) {
            return;
        }

        finishingSurvey.value = true;
        const status: FinishSurveyStatus = await store.dispatch('finishSurvey', { surveyId });
        finishingSurvey.value = false;

        if (status === FinishSurveyStatus.NotVerified || status === FinishSurveyStatus.NotCompleted) {
            return 'Survey could not be completed. Please refresh the browser and try again.';
        }
        return '';
    };

    const redirectToFinishPage = () => {
        router.push({
            name: 'survey-finish',
            params: { knowledgeModelAlias: store.getters.language.knowledgeModelAlias },
        });
    };

    const redirectToClosedPage = () => {
        router.push({
            name: 'survey-closed',
            params: { knowledgeModelAlias: store.getters.language.knowledgeModelAlias },
        });
    };

    return { finishSurvey, finishingSurvey, redirectToFinishPage, redirectToClosedPage, loadDemographicSurvey };
};

export const loadDemographicSurvey = async (surveyList: SurveyListResponse): Promise<Survey> => {
    const translationsToAdd: { language: Language; translations: Record<string, string> }[] = [];

    translationsToAdd['en'] = {
        language: { languageCode: 'en', key: 'en' } as Language,
        translations: {},
    };

    surveyList.demographicSurvey.questions.forEach((q: DemoGraphicSurveyQuestion) => {
        q.question.forEach((question) => {
            if (!translationsToAdd[question.languageCode]) {
                translationsToAdd[question.languageCode] = {
                    language: { languageCode: question.languageCode, key: question.languageCode } as Language,
                    translations: {},
                };
            }
            translationsToAdd[question.languageCode].translations['demographics.question.' + q.label] = question.question;
        });

        q.answerOptions.forEach((option) => {
            option.answer.forEach((answer) => {
                if (!translationsToAdd[answer.languageCode]) {
                    translationsToAdd[answer.languageCode] = {
                        language: { languageCode: answer.languageCode, key: answer.languageCode } as Language,
                        translations: {},
                    };
                }
                translationsToAdd[answer.languageCode].translations['demographics.answer.' + option.label] = answer.answer;
            });
        });
    });

    for (const language in translationsToAdd) {
        addTranslations(translationsToAdd[language].language, translationsToAdd[language].translations);
    }

    return new Survey({
        instrumentName: 'Demographic data',
        instrumentAlias: 'demographic_data',
        demographicsEnabled: true,
        sectionTitleKey: 'generic.DEMOGRAPHICS_SECTION_TITLE',
        sectionSubTitleKey: 'generic.DEMOGRAPHICS_SECTION_DESCRIPTION',
        surveyPriority: surveyList.demographicSurvey.surveyPriority,
        questions: surveyList.demographicSurvey.questions
            .map((q: DemoGraphicSurveyQuestion) => {
                return new Question({
                    id: q.id,
                    answerOptions: q.answerOptions.map((o) => {
                        return new Answer({
                            alias: o.label,
                            checked: q.value === o.label,
                        });
                    }),
                    isDemographic: true,
                    priority: surveyList.demographicSurvey.surveyPriority,
                    question: q.label,
                    questionType: q.questionType,
                    label: q.label,
                    answer: { value: q.value },
                    order: q.order,
                    value: q.value,
                    view: q.label.includes('default') ? q.label : q.view,
                });
            })
            .sort((a, b) => a.order - b.order),
    });
};
